import _ from 'lodash';
import {Service, Module} from 'app/decorators.js';
import R from 'app/resource';
import ReactModule from 'app/react/react';


/**
 * `CustomerStore` now delegates to the shared REST resource store from
 * `frontend/react` (see `resourceStore.ts`) instead of Restangular, so the
 * exact same `customers` collection/list state (list/count/loading/params) is
 * readable and writable from BOTH AngularJS and React.
 *
 * One behavioural difference: `get`/`create`/`update` now return plain data
 * objects instead of "live" Restangular elements, so they no longer have
 * `.put()`/`.remove()` methods on them directly - use `CustomerStore.update(id, data)`
 * / `CustomerStore.remove(id)` instead (see `customers/ctrls.js` EditInfoCtrl).
 */
@Module({
    name: 'app.store.customer',
    modules: [
        R,
        ReactModule,
    ],
})
@Service('CustomerStore')
export default class Store {
    constructor ($q, R, ReactBridge) {
        "ngInject";
        this.$q = $q;
        this.R = R;
        this.resourceStore = ReactBridge().getResourceStore('customers');
    }

    // Re-wraps a plain data object (as returned by the shared Zustand
    // resourceStore) into a Restangular element, so legacy consumers that
    // expect `.all()`/`.one()`/`.put()`/`.remove()` (e.g. `blnd-activity`
    // via `ActivityStore.getNew(object)`, which calls `object.all('activity')`)
    // keep working exactly as before.
    restangularize (obj) {
        return this.R.restangularizeElement(null, obj, 'customers');
    }

    // Live getters/setters backed directly by the shared Zustand store state
    // (rather than a mirrored copy) so external direct assignment - e.g.
    // `BlndTable` does `self.store.params = requestParams;` - stays in sync
    // with what `refresh()`/`loadMore()` actually read.
    get list () { return this.resourceStore.getState().list; }
    get count () { return this.resourceStore.getState().count; }
    get loading () { return this.resourceStore.getState().loading; }
    get isEmpty () { return this.resourceStore.getState().isEmpty; }
    get params () { return this.resourceStore.getState().params; }
    set params (value) { this.resourceStore.setState({params: value}); }

    resetParams () {
        this.resourceStore.getState().resetParams();
    }

    init () {
        this.resourceStore.getState().init();
    }

    refresh () {
        return this.$q.when(this.resourceStore.getState().refresh()).then(() => this);
    }

    loadMore () {
        return this.$q.when(this.resourceStore.getState().loadMore()).then(() => this);
    }

    get (id, params) {
        return this.$q.when(this.resourceStore.getState().get(_.toString(id), params))
            .then((data) => this.restangularize(data));
    }

    create (data) {
        return this.$q.when(this.resourceStore.getState().create(data));
    }

    update (id, data) {
        return this.$q.when(this.resourceStore.getState().update(_.toString(id), data));
    }

    remove (id) {
        return this.$q.when(this.resourceStore.getState().remove(_.toString(id)));
    }
}



// WEBPACK FOOTER //
// ./src/app/stores/customer.js